import moment from 'moment';
import type { DateRangePreset, Granularity } from '../../../types/timeframe';

/** The latest date for which monitored trip data is available. */
const maxAvailableDate = () => moment().subtract(2, 'days');

/** Daily custom ranges are capped to a 60-day (61 day inclusive) window. */
const MAX_DAILY_RANGE_DAYS = 60;

/**
 * Given a preset name, returns its corresponding ISO start/end dates.
 * End dates are capped to today - 2 days (data availability lag), matching
 * the calendar's own maxDate restriction in TimeframeFilter.
 */
export function computeDateRange(
  preset: DateRangePreset,
  granularity?: Granularity
): { startDate: string; endDate: string } {
  let startDate: string;
  let endDate: string;

  if (preset === 'custom' && granularity === 'daily') {
    // Daily only shows ~2 months of data, matching the 60-day custom-range cap.
    endDate = maxAvailableDate().format('YYYY-MM-DD');
    startDate = maxAvailableDate().subtract(MAX_DAILY_RANGE_DAYS, 'days').format('YYYY-MM-DD');
    return { startDate, endDate };
  }

  switch (preset) {
    case 'today':
      startDate = moment().format('YYYY-MM-DD');
      endDate = moment().format('YYYY-MM-DD');
      break;
    case 'yesterday':
      startDate = moment().subtract(1, 'day').format('YYYY-MM-DD');
      endDate = moment().subtract(1, 'day').format('YYYY-MM-DD');
      break;
    case 'thisWeek':
      startDate = moment().startOf('isoWeek').format('YYYY-MM-DD');
      endDate = moment().endOf('isoWeek').format('YYYY-MM-DD');
      break;
    case 'lastWeek':
      startDate = moment().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      endDate = moment().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD');
      break;
    case 'thisMonth':
      startDate = moment().startOf('month').format('YYYY-MM-DD');
      endDate = moment().endOf('month').format('YYYY-MM-DD');
      break;
    case 'lastMonth':
      startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
      break;
    case 'thisYear':
      startDate = moment().startOf('year').format('YYYY-MM-DD');
      endDate = moment().endOf('year').format('YYYY-MM-DD');
      break;
    case 'past12Months':
      startDate = moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD');
      endDate = moment().endOf('month').format('YYYY-MM-DD');
      break;
    case 'thisQuarter':
      startDate = moment().startOf('quarter').format('YYYY-MM-DD');
      endDate = moment().endOf('quarter').format('YYYY-MM-DD');
      break;
    case 'lastQuarter':
      startDate = moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD');
      endDate = moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD');
      break;
    case 'last4Quarters':
      startDate = moment().subtract(3, 'quarters').startOf('quarter').format('YYYY-MM-DD');
      endDate = moment().endOf('quarter').format('YYYY-MM-DD');
      break;
    case 'custom':
    default:
      startDate = moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD');
      endDate = moment().endOf('month').format('YYYY-MM-DD');
      break;
  }

  // Cap end date to the latest available data date (today - 2 days)
  const maxEnd = maxAvailableDate().format('YYYY-MM-DD');
  if (endDate > maxEnd) {
    endDate = maxEnd;
  }

  return { startDate, endDate };
}

/**
 * Helper to get quarter and year info from a date string
 */
export function getQuarterFromDate(dateStr: string): { quarter: number; year: number } {
  const m = moment(dateStr);
  return {
    quarter: m.quarter(),
    year: m.year(),
  };
}
